import React, { useEffect } from "react";
import Prism from "prismjs";
import "prismjs/themes/prism-tomorrow.css"; // Clean dark theme
// Include the language grammars you need
import "prismjs/components/prism-python";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-bash";

const CodeCopyEnhancer: React.FC = () => {
  useEffect(() => {
    const enhanceBlocks = () => {
      // Select <pre> elements created by CKEditor that haven't been wrapped yet
      const preElements = document.querySelectorAll<HTMLPreElement>(
        "pre:not(.enhanced-pre)",
      );

      preElements.forEach((pre) => {
        // Prevent mutating the DOM inside any active CKEditor or editable container
        if (
          pre.closest(
            ".ck, .ck-editor, .ck-editor__editable, .ck-content[contenteditable='true'], [contenteditable='true'], .cht-rich-text-wrapper",
          )
        ) {
          return;
        }

        const code = pre.querySelector("code");
        if (!code) return;

        pre.classList.add("enhanced-pre");

        // Extract language from class (e.g., class="language-python" -> "Python")
        const langClass = Array.from(code.classList).find((c) =>
          c.startsWith("language-"),
        );
        const langName = langClass
          ? langClass.replace("language-", "")
          : "Code";

        // Create header bar
        const header = document.createElement("div");
        header.className = "code-block-header";

        const langLabel = document.createElement("span");
        langLabel.innerText = langName;

        const copyBtn = document.createElement("button");
        copyBtn.className = "code-copy-btn";
        copyBtn.type = "button";
        copyBtn.innerText = "Copy";

        copyBtn.addEventListener("click", () => {
          const text = code.innerText || code.textContent || "";
          navigator.clipboard.writeText(text).then(() => {
            copyBtn.innerText = "Copied!";
            copyBtn.classList.add("copied");
            setTimeout(() => {
              copyBtn.innerText = "Copy";
              copyBtn.classList.remove("copied");
            }, 2000);
          });
        });

        header.appendChild(langLabel);
        header.appendChild(copyBtn);

        // Apply styling directly to pre and insert header before code
        // CRITICAL: Never reparent pre into an external wrapper div!
        // Reparenting detaches pre from React Virtual DOM parent, causing
        // "NotFoundError: Failed to execute 'removeChild' on 'Node'" when navigating or unmounting.
        pre.classList.add("code-block-wrapper");
        pre.insertBefore(header, code);

        // Run Prism syntax highlight on the newly mounted block
        Prism.highlightElement(code);
      });
    };

    enhanceBlocks();

    // Observe DOM mutations in case CKEditor content loads asynchronously
    const observer = new MutationObserver(() => {
      enhanceBlocks();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return null;
};

export default CodeCopyEnhancer;
